import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ProductModal } from '../components/ProductModal';
import { DoshiProduct } from '../types/product';

// Product data
interface Product {
  id: number;
  name: string;
  category: string;
  image: string;
  description: string;
  origin: string;
  usedFor: string[];
  shortDescription?: string;
  longDescription?: string;
  price?: number;
  traditionallyAyurvedicMedicine?:string[],
}

// Sample product data
  const allProducts: Product[] = [
  // Whole Spices
  {
    id: 1,
    name: 'Black Peppercorns',
    category: 'whole-spices',
    image: '/Whole Spice/black_peppercorns.jpg',
    description: 'Premium black peppercorns with a complex flavor profile and intense aroma.',
    origin: 'Vietnam',
    usedFor: ['Seasoning', 'Marinades', 'Sauces']
  },
  {
    id: 2,
    name: 'White Peppercorns',
    category: 'whole-spices',
    image: '/Whole Spice/whitepeppercone.jpeg',
    description: 'Smooth, earthy, and mildly spicy white peppercorns for delicate heat.',
    origin: 'Indonesia',
    usedFor: ['Light-colored sauces', 'Soups', 'Marinades']
  },
  {
    id: 3,
    name: 'Green Peppercorns',
    category: 'whole-spices',
    image: '/Whole Spice/fresh-green-pepper.webp',
    description: 'Fresh and mildly spicy green peppercorns with herbal undertones.',
    origin: 'India',
    usedFor: ['Pickling', 'Cream-based sauces', 'Thai and French dishes']
  },
  {
    id: 4,
    name: 'Pink Peppercorns',
    category: 'whole-spices',
    image: '/Whole Spice/pink-peppercorns.jpg',
    description: 'Fruity, mildly sweet pink peppercorns with a hint of spice.',
    origin: 'Brazil',
    usedFor: ['Salads', 'Desserts', 'Seafood']
  },
  {
    id: 5,
    name: 'Coriander Seeds',
    category: 'whole-spices',
    image: '/Whole Spice/Coriander-Seeds.jpg',
    description: 'Warm, citrusy coriander seeds perfect for enhancing curries and marinades.',
    origin: 'India',
    usedFor: ['Curries', 'Spice blends', 'Pickling']
  },
  {
    id: 6,
    name: 'Cumin Seeds',
    category: 'whole-spices',
    image: '/Whole Spice/cuminseeds.jpg',
    description: 'Aromatic cumin seeds with a deep, nutty, and slightly peppery taste.',
    origin: 'Iran',
    usedFor: ['Curries', 'Mexican dishes', 'Chili']
  },
  {
    id: 7,
    name: 'Caraway Seeds',
    category: 'whole-spices',
    image: '/Whole Spice/carawayseeds.jpeg',
    description: 'Earthy and slightly sweet caraway seeds with hints of anise.',
    origin: 'Netherlands',
    usedFor: ['Rye bread', 'Sauerkraut', 'Stews']
  },
  {
    id: 8,
    name: 'Fennel Seeds',
    category: 'whole-spices',
    image: '/Whole Spice/fennelseeds.jpg',
    description: 'Sweet, licorice-flavored fennel seeds ideal for teas and seasoning.',
    origin: 'India',
    usedFor: ['Herbal tea', 'Baking', 'Sausages']
  },
  {
    id: 9,
    name: 'Fenugreek Seeds',
    category: 'whole-spices',
    image: '/Whole Spice/fenugreek.jpg',
    description: 'Bittersweet, maple-like fenugreek seeds to elevate curries and spice blends.',
    origin: 'Turkey',
    usedFor: ['Curries', 'Pickling', 'Herbal remedies']
  },
  {
    id: 10,
    name: 'Mustard Seeds',
    category: 'whole-spices',
    image: '/Whole Spice/mustardseeds.jpg',
    description: 'Pungent and nutty mustard seeds for bold, zesty flavors.',
    origin: 'Canada',
    usedFor: ['Mustard condiments', 'Pickles', 'Curry pastes']
  },
  {
    id: 11,
    name: 'Nigella Seeds (Kalonji)',
    category: 'whole-spices',
    image: '/Whole Spice/nigellaseeds.jpg',
    description: 'Smoky, peppery nigella seeds with an onion-like taste.',
    origin: 'Egypt',
    usedFor: ['Flatbreads', 'Indian curries', 'Pickles']
  },
  {
    id: 12,
    name: 'Celery Seeds',
    category: 'whole-spices',
    image: '/Whole Spice/celeryseeds.jpeg',
    description: 'Sharp, earthy celery seeds with a concentrated herbal essence.',
    origin: 'China',
    usedFor: ['Salad dressings', 'Soups', 'Pickling']
  },
  {
    id: 13,
    name: 'Cardamom Pods',
    category: 'whole-spices',
    image: '/Whole Spice/cardomonseeds.jpeg',
    description: 'Fragrant green cardamom pods with citrusy, floral, and spicy notes.',
    origin: 'Guatemala',
    usedFor: ['Teas', 'Desserts', 'Curries']
  },
  {
    id: 14,
    name: 'Cinnamon Sticks',
    category: 'whole-spices',
    image: '/Whole Spice/cinnamonsticks.jpg',
    description: 'Warm, woody, and sweet cinnamon sticks for rich, aromatic flavor.',
    origin: 'Sri Lanka',
    usedFor: ['Baking', 'Teas', 'Curries']
  },
  {
    id: 15,
    name: 'Bay Leaves',
    category: 'whole-spices',
    image: '/Whole Spice/bayleaves.jpeg',
    description: 'Subtly floral bay leaves with hints of eucalyptus and spice.',
    origin: 'Turkey',
    usedFor: ['Soups', 'Stews', 'Sauces']
  },
  {
    id: 16,
    name: 'Dried Ginger',
    category: 'whole-spices',
    image: '/Whole Spice/driedginger.jpeg',
    description: 'Pungent and warming dried ginger for a zesty kick.',
    origin: 'India',
    usedFor: ['Teas', 'Curries', 'Baking']
  },
  {
    id: 17,
    name: 'Dried Galangal',
    category: 'whole-spices',
    image: '/Whole Spice/dried-galangal-root-kok-havlican.jpeg',
    description: 'Sharp and citrusy dried galangal with a peppery bite.',
    origin: 'Thailand',
    usedFor: ['Thai curries', 'Soups', 'Stir-fries']
  },
  // {
  //   id: 18,
  //   name: 'Licorice Root',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Licorice Root.jpeg',
  //   description: 'Naturally sweet licorice root with soothing and aromatic notes.',
  //   origin: 'Afghanistan',
  //   usedFor: ['Herbal teas', 'Natural sweeteners', 'Digestive remedies']
  // },
  // {
  //   id: 19,
  //   name: 'Cloves',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Cloves.jpeg',
  //   description: 'Bold, pungent cloves with a warm and slightly sweet aroma.',
  //   origin: 'Indonesia',
  //   usedFor: ['Baking', 'Curries', 'Mulled wine']
  // },
  // {
  //   id: 20,
  //   name: 'Star Anise',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Star Anise.jpeg',
  //   description: 'Strong, licorice-flavored star anise with a sweet-spicy punch.',
  //   origin: 'China',
  //   usedFor: ['Chinese dishes', 'Teas', 'Broths']
  // },
  // {
  //   id: 21,
  //   name: 'Mace Blades',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Mace Blades.jpeg',
  //   description: 'Delicate mace blades with a warm, nutmeg-like essence.',
  //   origin: 'Indonesia',
  //   usedFor: ['Baking', 'Meat rubs', 'Sauces']
  // },
  // {
  //   id: 22,
  //   name: 'Saffron',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Saffron.jpeg',
  //   description: 'Exquisite saffron threads with a deep floral aroma and golden hue.',
  //   origin: 'Iran',
  //   usedFor: ['Rice dishes', 'Desserts', 'Teas']
  // },
  // {
  //   id: 23,
  //   name: 'Allspice Berries',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Allspice Berries.jpeg',
  //   description: 'Rich allspice berries with notes of cloves, cinnamon, and nutmeg.',
  //   origin: 'Jamaica',
  //   usedFor: ['Marinades', 'Baking', 'Soups']
  // },
  // {
  //   id: 24,
  //   name: 'Capers (Dried)',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Capers (Dried).jpeg',
  //   description: 'Tangy and briny dried capers for a burst of Mediterranean flavor.',
  //   origin: 'Spain',
  //   usedFor: ['Salads', 'Pasta', 'Sauces']
  // },
  // {
  //   id: 25,
  //   name: 'Juniper Berries',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Juniper Berries.jpeg',
  //   description: 'Piney, slightly citrusy juniper berries, perfect for seasoning meats.',
  //   origin: 'Europe',
  //   usedFor: ['Gin making', 'Game meats', 'Pickling']
  // },
  // {
  //   id: 26,
  //   name: 'Long Pepper (Pippali)',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Long Pepper (Pippali).jpeg',
  //   description: 'Spicy and complex long pepper with hints of cinnamon.',
  //   origin: 'India',
  //   usedFor: ['Ayurvedic medicine', 'Curries', 'Stews']
  // },
  // {
  //   id: 27,
  //   name: 'Cubeb Pepper',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Cubeb Pepper.jpeg',
  //   description: 'Aromatic cubeb pepper with woody and slightly floral notes.',
  //   origin: 'Indonesia',
  //   usedFor: ['Spice blends', 'Herbal remedies', 'Perfumes']
  // },
  // {
  //   id: 28,
  //   name: 'Sumac Berries',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Sumac Berries.jpeg',
  //   description: 'Tangy, citrusy sumac berries to brighten up dishes.',
  //   origin: 'Middle East',
  //   usedFor: ['Salads', 'Meats', 'Dips']
  // },
  // {
  //   id: 29,
  //   name: 'Nutmeg',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Nutmeg.jpeg',
  //   description: 'Warm, nutty, and slightly sweet whole nutmeg for rich depth.',
  //   origin: 'Indonesia',
  //   usedFor: ['Baking', 'Creamy sauces', 'Desserts']
  // },
  // {
  //   id: 30,
  //   name: 'Dried Mango (Amchur Slices)',
  //   category: 'whole-spices',
  //   image: '/Whole Spice/Dried Mango (Amchur Slices).jpeg',
  //   description: 'Tangy dried mango slices for a natural sour kick.',
  //   origin: 'India',
  //   usedFor: ['Chutneys', 'Spice blends', 'Curries']
  // },
  
  
  // Ground Spices
  {
    id: 31,
    name: 'Ground Black Pepper',
    category: 'ground-spices',
    image: '/Ground Spice/Black-Pepper-ground-1.jpg',
    description: 'Finely ground black pepper with a bold, pungent, and complex flavor.',
    origin: 'Vietnam',
    usedFor: ['Seasoning', 'Soups', 'Marinades']
  },
  {
    id: 32,
    name: 'Ground White Pepper',
    category: 'ground-spices',
    image: '/Ground Spice/whitepepper.jpeg',
    description: 'Smooth and mildly spicy white pepper powder with earthy undertones.',
    origin: 'Indonesia',
    usedFor: ['Light-colored sauces', 'Soups', 'Marinades']
  },
  {
    id: 33,
    name: 'Ground Cumin',
    category: 'ground-spices',
    image: '/Ground Spice/cuminground.jpg',
    description: 'Warm, nutty, and slightly smoky cumin powder for rich, deep flavors.',
    origin: 'India',
    usedFor: ['Curries', 'Spice rubs', 'Mexican dishes']
  },
  {
    id: 34,
    name: 'Ground Coriander',
    category: 'ground-spices',
    image: '/Ground Spice/Corriander_Ground.webp',
    description: 'Aromatic and citrusy coriander powder with a mild, earthy flavor.',
    origin: 'Morocco',
    usedFor: ['Curries', 'Baked goods', 'Pickling']
  },
  {
    id: 35,
    name: 'Ground Turmeric',
    category: 'ground-spices',
    image: '/Ground Spice/Turmeric-Ground.jpg',
    description: 'Bright yellow turmeric powder with a warm, earthy, and slightly bitter taste.',
    origin: 'India',
    usedFor: ['Curries', 'Golden milk', 'Marinades']
  },
  {
    id: 36,
    name: 'Ground Cardamom',
    category: 'ground-spices',
    image: '/Ground Spice/ground-cardamom.jpg',
    description: 'Sweet and citrusy cardamom powder with floral and spicy notes.',
    origin: 'Guatemala',
    usedFor: ['Desserts', 'Teas', 'Middle Eastern dishes']
  },
  {
    id: 37,
    name: 'Ground Cloves',
    category: 'ground-spices',
    image: '/Ground Spice/cloves-whole-ground.jpg',
    description: 'Bold and pungent clove powder with a warm and slightly sweet aroma.',
    origin: 'Madagascar',
    usedFor: ['Baking', 'Spice blends', 'Meat dishes']
  },
  {
    id: 38,
    name: 'Ground Nutmeg',
    category: 'ground-spices',
    image: '/Ground Spice/nutmegground.jpg',
    description: 'Warm, nutty, and slightly sweet nutmeg powder for rich, deep flavors.',
    origin: 'Indonesia',
    usedFor: ['Baked goods', 'Sauces', 'Cream-based dishes']
  },
  {
    id: 39,
    name: 'Ground Cinnamon',
    category: 'ground-spices',
    image: '/Ground Spice/groundcinnamon.jpg',
    description: 'Sweet and woody cinnamon powder with warm, aromatic notes.',
    origin: 'Sri Lanka',
    usedFor: ['Baking', 'Hot beverages', 'Curries']
  },
  {
    id: 40,
    name: 'Ground Mace',
    category: 'ground-spices',
    image: '/Ground Spice/groundmace.jpeg',
    description: 'Delicate and aromatic mace powder with a warm, nutmeg-like essence.',
    origin: 'Indonesia',
    usedFor: ['Baking', 'Meat seasoning', 'Spice blends']
  },
  {
    id: 41,
    name: 'Ground Fennel',
    category: 'ground-spices',
    image: '/Ground Spice/fennelground.jpeg',
    description: 'Sweet and licorice-flavored fennel powder with a mild, herbal essence.',
    origin: 'Egypt',
    usedFor: ['Soups', 'Curries', 'Herbal teas']
  },
  {
    id: 42,
    name: 'Ground Ginger',
    category: 'ground-spices',
    image: '/Ground Spice/groundginger.jpeg',
    description: 'Pungent and slightly sweet ginger powder with a warm and spicy kick.',
    origin: 'China',
    usedFor: ['Baked goods', 'Herbal teas', 'Asian dishes']
  },
  {
    id: 43,
    name: 'Ground Mustard',
    category: 'ground-spices',
    image: '/Ground Spice/mustardground.jpeg',
    description: 'Spicy and tangy mustard powder with a pungent kick.',
    origin: 'Canada',
    usedFor: ['Dressings', 'Marinades', 'Pickling']
  },
  {
    id: 44,
    name: 'Ground Star Anise',
    category: 'ground-spices',
    image: '/Ground Spice/star-ground (2).jpeg',
    description: 'Strong and licorice-flavored star anise powder with a sweet-spicy punch.',
    origin: 'China',
    usedFor: ['Chinese five-spice', 'Soups', 'Teas']
  },
  {
    id: 45,
    name: 'Ground Sumac',
    category: 'ground-spices',
    image: '/Ground Spice/sumacground.jpg',
    description: 'Tangy and citrusy sumac powder to enhance bright and zesty flavors.',
    origin: 'Turkey',
    usedFor: ['Salads', 'Meats', 'Rice dishes']
  },
  {
    id: 46,
    name: 'Ground Allspice',
    category: 'ground-spices',
    image: '/Ground Spice/groundallspice.jpeg',
    description: 'Rich and aromatic allspice powder with notes of cloves, cinnamon, and nutmeg.',
    origin: 'Jamaica',
    usedFor: ['Baking', 'Marinades', 'Caribbean dishes']
  },
  {
    id: 47,
    name: 'Ground Celery Seed',
    category: 'ground-spices',
    image: '/Ground Spice/Ground-celery-seed.jpg',
    description: 'Sharp, earthy celery seed powder with concentrated herbal flavor.',
    origin: 'India',
    usedFor: ['Soups', 'Dressings', 'Pickling']
  },
  {
    id: 48,
    name: 'Ground Long Pepper (Pippali)',
    category: 'ground-spices',
    image: '/Ground Spice/groundlongpepper.jpg',
    description: 'Spicy and complex long pepper powder with hints of cinnamon.',
    origin: 'India',
    usedFor: ['Spice blends', 'Ayurvedic medicine', 'Curries']
  },
  {
    id: 49,
    name: 'Ground Licorice Root',
    category: 'ground-spices',
    image: '/Ground Spice/licoriariceroot.jpeg',
    description: 'Naturally sweet licorice root powder with soothing, aromatic notes.',
    origin: 'Afghanistan',
    usedFor: ['Teas', 'Candies', 'Herbal remedies']
  },
  {
    id: 50,
    name: 'Ground Dried Mango (Amchur)',
    category: 'ground-spices',
    image: '/Ground Spice/grounddriedmango.jpeg',
    description: 'Tangy dried mango powder for a natural sour kick in dishes.',
    origin: 'India',
    usedFor: ['Chutneys', 'Curries', 'Marinades']
  },
  
  // Spice Blends
  {
    id: 51,
    name: 'Garam Masala',
    category: 'spice-blends',
    image: '/Spice Blend/garammasala.jpg',
    description: 'A warm and aromatic blend of spices with hints of sweetness and heat.',
    origin: 'India',
    usedFor: ['Curries', 'Lentil dishes', 'Marinades']
  },
  {
    id: 52,
    name: 'Cajun Seasoning',
    category: 'spice-blends',
    image: '/Spice Blend/cajuan.webp',
    description: 'A bold and spicy blend with smoky, garlicky, and peppery flavors.',
    origin: 'USA (Louisiana)',
    usedFor: ['Grilled meats', 'Seafood', 'Fries']
  },
  {
    id: 53,
    name: 'Italian Seasoning',
    category: 'spice-blends',
    image: '/Spice Blend/italian.jpg',
    description: 'A fragrant blend of dried herbs with earthy, peppery, and floral notes.',
    origin: 'Italy',
    usedFor: ['Pasta dishes', 'Pizza', 'Soups']
  },
  {
    id: 54,
    name: 'Chinese Five Spice',
    category: 'spice-blends',
    image: '/Spice Blend/chineese.jpg',
    description: 'A balanced mix of sweet, sour, bitter, salty, and umami flavors.',
    origin: 'China',
    usedFor: ['Stir-fries', 'Roasted meats', 'Noodle soups']
  },
  {
    id: 55,
    name: 'Za\'atar',
    category: 'spice-blends',
    image: '/Spice Blend/zatar.webp',
    description: 'A tangy, nutty, and herby spice mix with sesame and sumac.',
    origin: 'Middle East',
    usedFor: ['Flatbreads', 'Salads', 'Roasted vegetables']
  },
  {
    id: 56,
    name: 'Tandoori Masala',
    category: 'spice-blends',
    image: '/Spice Blend/tandori.jpg',
    description: 'A smoky and spicy Indian blend with deep red color and bold flavors.',
    origin: 'India',
    usedFor: ['Grilled meats', 'Tandoori chicken', 'Marinades']
  },
  {
    id: 57,
    name: 'Chaat Masala',
    category: 'spice-blends',
    image: '/Spice Blend/chaatmasala.jpg',
    description: 'A tangy and salty spice blend with a hint of heat and umami.',
    origin: 'India',
    usedFor: ['Street food', 'Salads', 'Snacks']
  },
  {
    id: 58,
    name: 'Fines Herbes',
    category: 'spice-blends',
    image: '/Spice Blend/fines.jpg',
    description: 'A delicate and aromatic French herb blend with subtle floral notes.',
    origin: 'France',
    usedFor: ['Egg dishes', 'Light sauces', 'Salads']
  },
  {
    id: 59,
    name: 'Curry Powder',
    category: 'spice-blends',
    image: '/Spice Blend/curry.jpg',
    description: 'A versatile and flavorful blend with warm, earthy, and slightly spicy notes.',
    origin: 'India',
    usedFor: ['Curries', 'Soups', 'Rice dishes']
  },
  {
    id: 60,
    name: 'Peri Peri Seasoning',
    category: 'spice-blends',
    image: '/Spice Blend/periperi.jpeg',
    description: 'A hot and tangy spice blend with a fiery chili kick.',
    origin: 'Portugal & Africa',
    usedFor: ['Grilled chicken', 'Seafood', 'Marinades']
  },
  
  // Organic Spices
  // {
  //   id: 10,
  //   name: 'Organic Turmeric',
  //   category: 'organic-spices',
  //   image: 'https://images.unsplash.com/photo-1599690925058-90e1a0b56154?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  //   description: 'Vibrant yellow turmeric powder, grown organically for maximum health benefits.',
  //   origin: 'India',
  //   usedFor: ['Curries', 'Golden Milk', 'Health Supplements']
  // },
  // {
  //   id: 11,
  //   name: 'Organic Ginger',
  //   category: 'organic-spices',
  //   image: 'https://images.unsplash.com/photo-1615485290382-441e4d049cb5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  //   description: 'Organic ground ginger with a warm, spicy flavor.',
  //   origin: 'Peru',
  //   usedFor: ['Baking', 'Teas', 'Stir-fries']
  // },
  // {
  //   id: 12,
  //   name: 'Organic Cinnamon',
  //   category: 'organic-spices',
  //   image: 'https://images.unsplash.com/photo-1587131782738-de30ea91a542?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  //   description: 'Organic ground cinnamon with a sweet, woody flavor.',
  //   origin: 'Sri Lanka',
  //   usedFor: ['Baking', 'Beverages', 'Oatmeal']
  // },
  
  // Exotic Spices
  {
    id: 71,
    name: 'Premium Saffron',
    category: 'exotic-spices',
    image: '/Exotic Spice/saffron.jpg',
    description: 'The world\'s most precious spice, harvested from the stigmas of crocus flowers.',
    origin: 'Spain',
    usedFor: ['Paella', 'Risotto', 'Desserts']
  },
 
  {
    id: 74,
    name: 'Mahlab',
    category: 'exotic-spices',
    image: '/Exotic Spice/mahlab.jpg',
    description: 'A unique spice made from cherry pits with a complex cherry-almond flavor.',
    origin: 'Middle East',
    usedFor: ['Baked goods', 'Traditional sweets', 'Bread']
  },
  {
    id: 75,
    name: 'Black Cardamom',
    category: 'exotic-spices',
    image: '/Exotic Spice/blackcardomom.jpeg',
    description: 'Large pods with a strong smoky flavor and intense aroma.',
    origin: 'Nepal',
    usedFor: ['Slow-cooked meats', 'Rice dishes', 'Spice blends']
  },
  {
    id: 76,
    name: 'Ajwain Seeds',
    category: 'exotic-spices',
    image: '/Exotic Spice/ajwain.jpeg',
    description: 'Tiny seeds with a thyme-like flavor and aromatic properties.',
    origin: 'India',
    usedFor: ['Curries', 'Flatbreads', 'Digestive remedies']
  },
  {
    id: 77,
    name: 'Tonka Beans',
    category: 'exotic-spices',
    image: '/Exotic Spice/tonka.webp',
    description: 'Fragrant beans with vanilla, cherry, and almond notes.',
    origin: 'Brazil',
    usedFor: ['Desserts', 'Infusions', 'Chocolates']
  },
  {
    id: 78,
    name: 'Sumac',
    category: 'exotic-spices',
    image: '/Exotic Spice/sumac.jpeg',
    description: 'Ground berries with a tart, citrusy flavor.',
    origin: 'Turkey',
    usedFor: ['Salads', 'Meats', 'Hummus']
  },
  {
    id: 79,
    name: 'Cubeb Pepper',
    category: 'exotic-spices',
    image: '/Exotic Spice/cubeb.jpg',
    description: 'Tailed pepper with complex pine and allspice notes.',
    origin: 'Indonesia',
    usedFor: ['Spice blends', 'Marinades', 'Tea infusions']
  },
  {
    id: 80,
    name: 'Pink Peppercorns',
    category: 'exotic-spices',
    image: '/Exotic Spice/pinkpepper.jpg',
    description: 'Delicate berries with a sweet-spicy flavor.',
    origin: 'Brazil',
    usedFor: ['Gourmet dishes', 'Salads', 'Cocktails']
  },
  {
    id: 81,
    name: 'Galangal',
    category: 'exotic-spices',
    image: '/Exotic Spice/galangal.jpg',
    description: 'A rhizome similar to ginger with citrusy and pine-like notes.',
    origin: 'Thailand',
    usedFor: ['Thai curries', 'Soups', 'Stir-fries']
  },
  {
    id: 82,
    name: 'Amchur',
    category: 'exotic-spices',
    image: '/Exotic Spice/amchoor.jpeg',
    description: 'Dried mango powder with a tangy, fruity flavor.',
    origin: 'India',
    usedFor: ['Chutneys', 'Curries', 'Seasonings']
  },
  {
    id: 83,
    name: 'Nigella Seeds',
    category: 'exotic-spices',
    image: '/Exotic Spice/nigellaseeds.jpeg',
    description: 'Small black seeds with an onion-like flavor.',
    origin: 'India',
    usedFor: ['Bread toppings', 'Pickling', 'Spice mixes']
  },
  {
    id: 84,
    name: 'Berbere Spice',
    category: 'exotic-spices',
    image: '/Exotic Spice/berbera.jpeg',
    description: 'A complex, hot spice blend with warm and aromatic notes.',
    origin: 'Ethiopia',
    usedFor: ['Stews', 'Lentils', 'Roasted meats']
  },
  {
    id: 85,
    name: 'Dukkah',
    category: 'exotic-spices',
    image: '/Exotic Spice/dukkah.jpeg',
    description: 'A nutty and aromatic blend of nuts, seeds, and spices.',
    origin: 'Egypt',
    usedFor: ['Dips', 'Crusts', 'Bread toppings']
  },
  {
    id: 86,
    name: 'Urfa Biber',
    category: 'exotic-spices',
    image: '/Exotic Spice/urfa.jpeg',
    description: 'Dried Turkish chili with smoky, raisin-like notes.',
    origin: 'Turkey',
    usedFor: ['Kebabs', 'Roasted vegetables', 'Mediterranean dishes']
  },
 

  // Doshi Ayucare Products
  
    {
      id: 87,
      name: 'Amla Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/Amla.png',
      description: 'Rich in Vitamin C, Supports Digestive Health, Promotes Healthy Skin and Hair, Powerful Antioxidant, Boosts Metabolism, Regulates Blood Sugar Levels, Enhances Eye Health, Natural Anti-inflammatory, Supports Heart Health',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for individuals looking to boost their immunity',
        'Beneficial for those managing diabetes',
        'Suitable for people seeking natural skincare and haircare solutions',
        'Perfect for individuals aiming for weight management',
        'Useful for those with inflammatory conditions like arthritis'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Amla, also known as "Indian Gooseberry," has been a cornerstone of Ayurvedic medicine for thousands of years',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Rich in Vitamin C: Traditionally valued for its high Vitamin C content, boosting immunity and overall health',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Amla Powder'
      ]
    },
    {
      id: 88,
      name: 'Arjuna Bark Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/arjuna.png',
      description: 'Supports Heart Health, Regulates Blood Pressure, Improves Cardiovascular Function, Enhances Circulation, Anti-inflammatory Properties, Boosts Immunity, Promotes Liver Health, Reduces Cholesterol Levels',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for Heart Health Support',
        'Beneficial for Blood Pressure Regulation',
        'Suitable for Cardiovascular Health',
        'Great for Circulatory System',
        'Helpful for Liver Function'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Arjuna Bark has been used for thousands of years in Ayurvedic medicine, revered for its cardioprotective properties',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Heart Health: Traditionally used to support cardiovascular health, strengthen the heart muscles, and improve blood circulation',
        'Blood Pressure Regulation: Valued for its ability to help regulate blood pressure and maintain healthy levels',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Arjuna Bark Powder'
      ]
    },
    {
      id: 89,
      name: 'Ashwagandha Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/aswagandha.png',
      description: 'Boosts Immunity, Reduces Stress and Anxiety, Improves Energy and Stamina, Supports Cognitive Function, Enhances Sleep Quality, Supports Weight Management',
      origin: 'Natural & Safe',
      usedFor: [
        'Particularly beneficial for women',
        'Ideal for athletes and individuals with high physical activity levels',
        'Beneficial for students and professionals',
        'Contributes to heart health',
        'Helps in weight management'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Ashwagandha, also known as "Indian Ginseng," has been used for over 3,000 years in Ayurvedic medicine for its extensive health benefits',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Stress Relief: Traditionally used as an adaptogen to help the body manage stress and reduce anxiety',
        'Sleep Aid: Traditionally used to improve sleep quality and combat insomnia',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Ashwagandha Powder'
      ]
    },
    {
      id: 90,
      name: 'Haritaki Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/haritaki.png',
      description: 'Supports Digestive Health, Relieves Constipation, Detoxifies the Body, Anti-inflammatory Properties, Boosts Immunity, Improves Oral Health, Promotes Healthy Skin and Hair, Regulates Blood Sugar Levels',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for Digestive Support',
        'Beneficial for Constipation Relief',
        'Suitable for Detoxification',
        'Great for Immune Support',
        'Helpful for Managing Blood Sugar Levels'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Haritaki, also known as "Harad," has been used for thousands of years in Ayurvedic medicine for its diverse health benefits',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Digestive Health: Traditionally used to support digestive health, alleviate constipation, and promote regular bowel movements',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Haritaki Powder'
      ]
    },
    {
      id: 91,
      name: 'Moringa Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/moringa.png',
      description: 'Rich in Nutrients and Antioxidants, Boosts Energy Levels, Supports Immune System, Improves Digestive Health, Promotes Healthy Skin and Hair, Reduces Inflammation, Regulates Blood Sugar Levels, Enhances Cognitive Function',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for Overall Wellness',
        'Beneficial for Energy Boost',
        'Suitable for Immune Support',
        'Great for Digestive Health',
        'Helpful for Skin and Hair Health'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Moringa has been used for centuries in Ayurvedic medicine for its vast range of health benefits',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Vital Nutrients: Known as "Shigru" in Ayurveda, it is valued for its nutrient-rich profile, providing essential vitamins and minerals',
        'Detoxification: Traditionally used to detoxify and cleanse the body, aiding in the removal of toxins',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Moringa Powder'
      ]
    },
    {
      id: 92,
      name: 'Triphala Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/trifala.png',
      description: 'Aids Digestion, Promotes Gut Health, Boosts Immunity, Supports Detoxification, Rich in Antioxidants, Enhances Vision, Regulates Blood Sugar, Anti-inflammatory Properties',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for Digestive Health',
        'Beneficial for Immune Support',
        'Suitable for Weight Management',
        'Great for Eye Health',
        'Helpful for Managing Diabetes'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Triphala, a blend of three fruits (Amla, Haritaki, and Bibhitaki), has been used for thousands of years in Ayurvedic medicine for its comprehensive health benefits',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Digestive Health: Traditionally used to support digestive health, relieve constipation, and maintain regular bowel movements',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Triphala Powder'
      ]
    },
    {
      id: 93,
      name: 'Giloy Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/gilloy.png',
      description: 'Boosts Immunity, Enhances Digestion, Reduces Stress and Anxiety, Anti-inflammatory, Detoxifies the Body, Regulates Blood Sugar, Promotes Healthy Skin, Supports Respiratory Health',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for Immune Support',
        'Beneficial for Digestive Health',
        'Suitable for Stress Relief',
        'Great for Skin Health',
        'Helpful for Managing Diabetes'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Giloy, also known as "Amrita" or "Guduchi," has been used for thousands of years in Ayurvedic medicine for its numerous health benefits',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Immunity Booster: Traditionally used to strengthen the immune system and enhance the body\'s resistance to infections',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Giloy Powder'
      ]
    },
    {
      id: 94,
      name: 'Kaunch Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/kaunch.png',
      description: 'Boosts Libido, Enhances Fertility, Supports Muscle Growth, Reduces Stress and Anxiety, Anti-inflammatory Properties, Improves Cognitive Function, Supports Nervous System, Enhances Stamina and Vitality',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for Reproductive Health',
        'Beneficial for Athletes',
        'Suitable for Stress Relief',
        'Great for Cognitive Health',
        'Helpful for Nervous System Support'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Kaunch, also known as "Kapikachhu" or "Mucuna Pruriens," has been used for thousands of years in Ayurvedic medicine for its diverse health benefits',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Reproductive Health: Traditionally used to enhance male fertility, boost libido, and support reproductive health',
        'Nervous System Support: Valued for its neuroprotective properties, supporting the health and function of the nervous system',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Kaunch Powder'
      ]
    },
    {
      id: 95,
      name: 'Sonth (Dry Ginger) Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/sonth.png',
      description: 'Aids Digestion, Relieves Cold and Cough, Anti-inflammatory, Boosts Immunity, Regulates Blood Sugar, Enhances Metabolism, Promotes Heart Health, Provides Pain Relief',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for Digestive Health',
        'Beneficial for Cold and Flu',
        'Suitable for Weight Management',
        'Great for Pain Relief',
        'Helpful for Diabetics'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Sonth, or Dry Ginger, has been used for thousands of years in Ayurvedic medicine for its extensive health benefits',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Digestive Aid: Traditionally used to enhance digestion, alleviate indigestion, gas, and bloating',
        'Cold and Cough Relief: Known for its effectiveness in soothing sore throats, reducing coughs, and clearing nasal congestion',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Sonth (Dry Ginger) Powder'
      ]
    },
    {
      id: 96,
      name: 'Shatavari Powder',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/shatarvi.png',
      description: 'Supports Women\'s Health, Enhances Reproductive Health, Boosts Immunity, Aids Digestion, Anti-inflammatory, Balances Hormones, Promotes Lactation, Improves Energy and Vitality',
      origin: 'Natural & Safe',
      usedFor: [
        'Ideal for Women\'s Health',
        'Beneficial for Reproductive Health',
        'Suitable for Postpartum Recovery',
        'Great for Hormonal Balance',
        'Helpful for Immune Support'
      ],
      traditionallyAyurvedicMedicine: [
        'Ancient Wisdom: Shatavari, known as the "Queen of Herbs," has been used for thousands of years in Ayurvedic medicine to support women\'s health',
        'Holistic Healing: Recognized in Ayurveda for balancing the three doshas (Vata, Pitta, Kapha), promoting overall health and well-being',
        'Women\'s Health: Traditionally used to support female reproductive health, including menstrual and menopausal health',
        'Sustainable and Ethical: Embraced for its sustainability and ethical sourcing, aligning with Ayurvedic principles of living in harmony with nature',
        'Centuries of Expertise: Backed by a century of experience in Ayurveda, ensuring the authenticity, purity, and quality of our Shatavari Powder'
      ]
    },
    {
      id: 97,
      name: 'Madhucare Herbal Supplement For Diabetes',
      category: 'doshi-ayucare',
      image: '/Doshi Ayucare/madhucare.png',
      description: 'Herbal Diabetes Supplement: Specifically formulated for diabetic patients, Blood Sugar Management: Helps regulate healthy blood sugar levels, Natural Ingredients: Free from synthetic chemicals and additives, Safe and Non-Habit Forming: Minimal side effects, long-term use without dependency, Quality Assured: High-quality herbs under stringent standards, Trusted Tradition: Backed by centuries of Ayurvedic knowledge',
      origin: 'Natural & Safe',
      usedFor: [
        'Diabetes Management: Designed to assist individuals managing diabetes',
        'Holistic Health: Ideal for those seeking a natural, comprehensive approach to wellness',
        'Daily Wellness Routine: Suitable for incorporation into daily health practices'
      ],
      traditionallyAyurvedicMedicine: [
        'Centuries-Old Wisdom: Formulated using 21 revered Ayurvedic herbs known for their efficacy in diabetes management',
        'Herbal Potency: Harnesses the potency of herbs traditionally used to promote metabolic health and support optimal organ function',
        'Herbal Synergy: Synergistic blend of herbs chosen for their complementary actions in promoting healthy blood sugar levels',
        'Balanced Dosha Support: Supports balanced Vata, Pitta, and Kapha doshas, essential for maintaining health and vitality',
        'Trusted Heritage: Rooted in a rich heritage of Ayurvedic knowledge and practice, passed down through generations'
      ]
    }
  
];

// Category information
const categoryInfo: {[key: string]: {title: string, description: string, image: string}} = {
  'whole-spices': {
    title: 'Whole Spices',
    description: 'Discover our collection of premium whole spices, carefully sourced from the finest growing regions around the world.',
    image: '/Category/whole.avif',
  },
  'ground-spices': {
    title: 'Ground Spices',
    description: 'Our ground spices are freshly milled to preserve their authentic flavors and aromas.',
    image: '/Category/ground.avif',
  },
  'spice-blends': {
    title: 'Spice Blends',
    description: 'Expertly crafted combinations of spices for specific cuisines and dishes.',
    image: '/Category/blends.jpeg',
  },
  // 'organic-spices': {
  //   title: 'Organic Spices',
  //   description: 'Certified organic spices grown without synthetic pesticides or fertilizers.',
  //   image: 'https://images.unsplash.com/photo-1599690925058-90e1a0b56154?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
  // },
  'exotic-spices': {
    title: 'Exotic Spices',
    description: 'Our collection of rare and unique spices from remote regions of the world.',
    image: '/Category/exotic.avif',
  },
  'doshi-ayucare': {
    title: 'Doshi Ayucare',
    description: 'Premium Ayurvedic personal care products for holistic wellness, crafted with traditional herbs and natural ingredients.',
    image: '/Category/doshi.png',
  },
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

// First, let's define a function to convert between product types
const convertToModalProduct = (product: Product | DoshiProduct): Product => {
  if ('longDescription' in product) {
    // It's a DoshiProduct
    return {
      id: Number(product.id), // Convert string ID to number
      name: product.name,
      category: product.category,
      image: product.image,
      description: product.longDescription || product.shortDescription || '',
      price: product.price || 0,
      // Add any other required fields with sensible defaults
      origin: '', 
      usedFor: []
    };
  } else {
    // It's already a Product
    return product;
  }
};

const ProductCategory: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [categoryData, setCategoryData] = useState<{title: string, description: string, image: string} | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | DoshiProduct | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (category) {
      // Filter products by category
      const filteredProducts = allProducts.filter(product => product.category === category);
      setProducts(filteredProducts);
      
      // Set category information
      setCategoryData(categoryInfo[category]);
    }
  }, [category]);

  const openModal = (product: Product | DoshiProduct) => {
    // Store the original product
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  if (!categoryData) {
    return (
      <div className="container-custom py-20 text-center">
        <h2 className="text-2xl font-bold">Loading...</h2>
      </div>
    );
  }

  return (
    <div className="bg-primary-50">
      {/* Hero Section */}
      <section className="relative bg-spice-brown text-white py-20">
        <div className="absolute inset-0 bg-black opacity-40" style={{ backgroundImage: `url(${categoryData.image})`, backgroundSize: 'cover', backgroundPosition: 'center', mixBlendMode: 'multiply' }}></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{categoryData.title}</h1>
            <p className="text-xl text-primary-100">
              {categoryData.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold mb-4">Our {categoryData.title}</h2>
            <p className="text-lg text-primary-600 max-w-2xl mx-auto">
              Explore our selection of premium {categoryData.title.toLowerCase()}, carefully sourced and processed to preserve their authentic flavors.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {products.map((product) => (
              <motion.div 
                key={product.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                variants={itemVariants}
              >
                <div className="h-64 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{product.name}</h3>
                  <p className="text-primary-600 mb-4">{product.description}</p>
                  
                  {/* <div className="mb-4">
                    <span className="text-sm font-medium text-primary-700">Origin: </span>
                    <span className="text-sm text-primary-600">{product.origin}</span>
                  </div> */}
                  
                  <div className="mb-4">
                    <span className="text-sm font-medium text-primary-700">Targeted Use: </span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {product.usedFor.map((use, index) => (
                        <span 
                          key={index} 
                          className="inline-block bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded"
                        >
                          {use}
                        </span>
                      ))}
                    </div>
                  </div>

                  {category === 'doshi-ayucare' && (
                    <button
                      onClick={() => openModal(product)}
                      className="w-full px-4 py-2 bg-spice-red text-white rounded-md hover:bg-spice-red-dark transition-colors duration-300"
                    >
                      Learn More
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-12">
            <Link to="/products" className="btn btn-secondary px-6 py-3">
              Back to All Categories
            </Link>
          </div>
        </div>
      </section>

      {/* Related Categories */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-bold mb-8 text-center">Explore Other Categories</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(categoryInfo)
              .filter(([key]) => key !== category)
              .slice(0, 4)
              .map(([key, value]) => (
                <Link 
                  key={key} 
                  to={`/products/${key}`}
                  className="group block rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="relative h-32">
                    <img 
                      src={value.image} 
                      alt={value.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <h3 className="text-white font-bold text-lg">{value.title}</h3>
                    </div>
                  </div>
                </Link>
              ))
            }
          </div>
        </div>
      </section>

      {/* Modal */}
      {isModalOpen && selectedProduct && (
        <ProductModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          product={convertToModalProduct(selectedProduct)}
        />
      )}
    </div>
  );
};

export default ProductCategory; 